import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { FaPlay, FaVolumeHigh, FaVolumeXmark } from "react-icons/fa6";
import type { Message } from "../types/MessageListType";
import { blurHashToDataUrl } from "../utils/blurHashPaint";
import {
  aspectPaddingBottomPct,
  fitMediaInside,
} from "../utils/mediaBubbleBox";
import { resolveMediaDims } from "../utils/mediaDims";
import {
  getBubbleAutoplayUrl,
  shouldAutoplayInBubble,
  toSameOriginMediaUrl,
} from "../utils/mediaPlayUrl";
import TgMediaProgressRing from "./TgMediaProgressRing";
import TgMediaRetryButton from "./TgMediaRetryButton";

type Props = {
  message: Message;
  className?: string;
  compact?: boolean;
  /** Media + caption: slightly tighter max box so bubble doesn't tower over chat */
  withCaption?: boolean;
  onOpen: () => void;
};

function pickBlurHoldMs() {
  return 40 + Math.floor(Math.random() * (1000 - 40 + 1));
}

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.max(0, Math.ceil(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function parseDurationLabel(raw?: string | null): number | null {
  if (!raw) return null;
  const m = String(raw).trim().match(/^(\d+):([0-5]?\d)$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function resolveDurationSec(message: Message): number {
  const ms = Number(
    message.video_duration ?? message.videoDuration ?? message.duration,
  );
  if (Number.isFinite(ms) && ms > 0) return ms / 1000;
  return (
    parseDurationLabel(
      message.video_duration_label || message.videoDurationLabel,
    ) || 0
  );
}

/**
 * Video bubble: dims-first → blur → thumb → optional autoplay.
 * Cancel (X) keeps blur + retry arrow; retry restarts download.
 */
export default function ChatVideoBubble({
  message,
  className = "",
  compact = false,
  withCaption = false,
  onOpen,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const holdMsRef = useRef(pickBlurHoldMs());
  const cancelledRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [thumbDecoded, setThumbDecoded] = useState(false);
  const [holdDone, setHoldDone] = useState(false);
  const [thumbRevealed, setThumbRevealed] = useState(false);
  const [keepPlaceholder, setKeepPlaceholder] = useState(true);
  const [keepRing, setKeepRing] = useState(true);
  const [cancelled, setCancelled] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  /** Remaining seconds while autoplay runs (counts down). */
  const [remainSec, setRemainSec] = useState<number | null>(null);
  /** Sound off by default (autoplay policy); toggle only for this bubble. */
  const [soundOn, setSoundOn] = useState(false);

  const blurHash = message.blur_hash || message.blurHash || null;
  const totalSec = useMemo(() => resolveDurationSec(message), [
    message.video_duration,
    message.videoDuration,
    message.duration,
    message.video_duration_label,
    message.videoDurationLabel,
  ]);
  const blurUrl = useMemo(
    () => (blurHash ? blurHashToDataUrl(blurHash, 32, 32) : null),
    [blurHash],
  );

  const dbDims = resolveMediaDims(message);
  /**
   * Dims-first: if DB gave WxH — NEVER resize after decode (TG immutable layout).
   * Measure only when dims missing (legacy / probe-failed stub).
   */
  const hasReliableDims = !!dbDims && dbDims.w >= 64 && dbDims.h >= 64;
  const [stubDims, setStubDims] = useState<{ w: number; h: number } | null>(
    null,
  );
  const dims = hasReliableDims ? dbDims : stubDims || dbDims;
  const maxBox = compact
    ? { maxW: 288, maxH: 192 }
    : withCaption
      ? { maxW: 320, maxH: 320 }
      : { maxW: 384, maxH: 448 };
  const box = useMemo(() => {
    if (dims) return fitMediaInside(dims.w, dims.h, maxBox.maxW, maxBox.maxH);
    return {
      width: Math.min(240, maxBox.maxW),
      height: Math.min(180, maxBox.maxH),
    };
  }, [dims, maxBox.maxW, maxBox.maxH]);

  const padPct = useMemo(() => {
    if (dims) return aspectPaddingBottomPct(dims.w, dims.h);
    return aspectPaddingBottomPct(box.width, box.height);
  }, [dims, box.width, box.height]);

  const adoptStubOnly = (nw: number, nh: number) => {
    if (hasReliableDims) return;
    if (!(nw > 0) || !(nh > 0)) return;
    setStubDims((prev) =>
      prev && prev.w === nw && prev.h === nh ? prev : { w: nw, h: nh },
    );
  };

  const wantAutoplay = shouldAutoplayInBubble(message);
  const autoplayUrl = useMemo(() => {
    if (!wantAutoplay) return "";
    return toSameOriginMediaUrl(getBubbleAutoplayUrl(message));
  }, [
    wantAutoplay,
    message.media_url,
    message.message_content,
    message.media_hls,
    message.media_mp4_1080,
    message.media_mp4_720,
    message.media_hls_tier,
    message.media_process_status,
    message.video_size_bytes,
    message.videoSizeBytes,
  ]);

  const staticDurationLabel =
    message.video_duration_label ||
    message.videoDurationLabel ||
    (totalSec > 0 ? formatClock(totalSec) : null);

  const thumbUrl = message.message_thumbnail || "";
  const lqipSrc =
    !blurUrl &&
    message.message_preview &&
    message.message_preview !== thumbUrl
      ? message.message_preview
      : "";

  const showPlaceholder =
    (keepPlaceholder || cancelled) && Boolean(blurUrl || lqipSrc);

  useEffect(() => {
    cancelledRef.current = false;
    holdMsRef.current = pickBlurHoldMs();
    setCancelled(false);
    setThumbDecoded(false);
    setHoldDone(false);
    setThumbRevealed(false);
    setKeepPlaceholder(true);
    setKeepRing(true);
    setVideoReady(false);
    setVideoFailed(false);
    setRemainSec(totalSec > 0 ? totalSec : null);
    setStubDims(null);
    setSoundOn(false);
    const t = window.setTimeout(() => {
      if (!cancelledRef.current) setHoldDone(true);
    }, holdMsRef.current);
    return () => window.clearTimeout(t);
  }, [thumbUrl, blurUrl, lqipSrc, autoplayUrl, loadKey, totalSec]);

  useEffect(() => {
    if (cancelled) return;
    if (thumbDecoded && holdDone) setThumbRevealed(true);
  }, [thumbDecoded, holdDone, cancelled]);

  useEffect(() => {
    if (cancelled) return;
    if (!thumbRevealed && !videoReady) return;
    const t = window.setTimeout(() => setKeepPlaceholder(false), 320);
    const t2 = window.setTimeout(() => setKeepRing(false), 420);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [thumbRevealed, videoReady, cancelled]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !wantAutoplay || !autoplayUrl || cancelled) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(!!entry?.isIntersecting),
      { rootMargin: "120px", threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [wantAutoplay, autoplayUrl, cancelled]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !wantAutoplay || !autoplayUrl || cancelled) return;

    if (!inView) {
      try {
        video.pause();
      } catch {
        /* */
      }
      return;
    }

    const current = video.getAttribute("src") || "";
    if (current !== autoplayUrl) {
      video.src = autoplayUrl;
      video.load();
    }
    video.muted = !soundOn;
    video.playsInline = true;
    video.loop = true;
    void video.play().catch(() => {});
  }, [inView, wantAutoplay, autoplayUrl, cancelled, loadKey, soundOn]);

  /** Countdown while muted autoplay is running. */
  useEffect(() => {
    if (!videoReady || !wantAutoplay || cancelled || videoFailed) {
      setRemainSec(totalSec > 0 ? totalSec : null);
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    const tick = () => {
      const dur =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : totalSec;
      if (!(dur > 0)) return;
      const left = Math.max(0, dur - (video.currentTime || 0));
      setRemainSec(left);
    };

    tick();
    video.addEventListener("timeupdate", tick);
    video.addEventListener("seeked", tick);
    video.addEventListener("playing", tick);
    const raf = window.setInterval(tick, 250);
    return () => {
      video.removeEventListener("timeupdate", tick);
      video.removeEventListener("seeked", tick);
      video.removeEventListener("playing", tick);
      window.clearInterval(raf);
    };
  }, [videoReady, wantAutoplay, cancelled, videoFailed, totalSec, loadKey]);

  const handleCancel = () => {
    cancelledRef.current = true;
    setCancelled(true);
    setThumbDecoded(false);
    setHoldDone(false);
    setThumbRevealed(false);
    setKeepRing(false);
    setKeepPlaceholder(true);
    setVideoReady(false);
    setInView(false);
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
      } catch {
        /* */
      }
      video.removeAttribute("src");
      try {
        video.load();
      } catch {
        /* */
      }
    }
  };

  const handleRetry = () => {
    cancelledRef.current = false;
    setLoadKey((k) => k + 1);
  };

  const toggleSound = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    const next = !soundOn;
    setSoundOn(next);
    if (!video) return;
    video.muted = !next;
    if (next) {
      try {
        video.volume = 1;
      } catch {
        /* */
      }
      void video.play().catch(() => {});
    }
  };

  const coverReady = !cancelled && (thumbRevealed || videoReady);
  const showPlayOverlay =
    coverReady &&
    !keepRing &&
    (!videoReady || videoFailed || !wantAutoplay);

  const timerLabel =
    videoReady && wantAutoplay && !cancelled && remainSec != null
      ? formatClock(remainSec)
      : staticDurationLabel;

  return (
    <div
      ref={wrapRef}
      role="button"
      tabIndex={0}
      onClick={() => {
        if (cancelled) {
          handleRetry();
          return;
        }
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (cancelled) handleRetry();
          else onOpen();
        }
      }}
      className={`relative block max-w-full cursor-pointer overflow-hidden ${className}`}
      style={{
        width: box.width,
        maxWidth: "100%",
      }}
    >
      {/* Immutable slot — padding-% of width (TG dims-first, no decode wait) */}
      <div
        aria-hidden
        className="pointer-events-none block w-full"
        style={{ paddingBottom: `${padPct}%`, height: 0 }}
      />
      {showPlaceholder && blurUrl && (
        <img
          src={blurUrl}
          alt=""
          aria-hidden
          draggable={false}
          width={dims?.w ?? box.width}
          height={dims?.h ?? box.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: coverReady ? 0 : 1,
            filter: "blur(14px)",
            transform: "scale(1.08)",
            transition: "opacity 280ms ease",
          }}
        />
      )}

      {showPlaceholder && !blurUrl && lqipSrc && (
        <img
          src={lqipSrc}
          alt=""
          aria-hidden
          draggable={false}
          decoding="async"
          width={dims?.w ?? box.width}
          height={dims?.h ?? box.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: coverReady ? 0 : 1,
            filter: "blur(8px)",
            transform: "scale(1.04)",
            transition: "opacity 240ms ease",
          }}
        />
      )}

      {thumbUrl && !cancelled && (
        <img
          key={`thumb-${loadKey}`}
          src={thumbUrl}
          alt=""
          aria-hidden
          draggable={false}
          decoding="async"
          width={dims?.w ?? box.width}
          height={dims?.h ?? box.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: videoReady ? 0 : thumbRevealed ? 1 : 0,
            transition: "opacity 280ms ease",
          }}
          onLoad={(e) => {
            if (cancelledRef.current) return;
            setThumbDecoded(true);
            const el = e.currentTarget;
            adoptStubOnly(el.naturalWidth, el.naturalHeight);
          }}
          onError={() => setThumbDecoded(false)}
        />
      )}

      {wantAutoplay && autoplayUrl && !videoFailed && !cancelled && (
        <video
          key={`vid-${loadKey}`}
          ref={videoRef}
          muted={!soundOn}
          playsInline
          loop
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            opacity: videoReady ? 1 : 0,
            transition: "opacity 320ms ease",
          }}
          onLoadedData={(e) => {
            if (cancelledRef.current) return;
            setVideoReady(true);
            const v = e.currentTarget;
            v.muted = !soundOn;
            adoptStubOnly(v.videoWidth, v.videoHeight);
          }}
          onPlaying={() => {
            if (!cancelledRef.current) setVideoReady(true);
          }}
          onError={() => {
            setVideoFailed(true);
            setVideoReady(false);
          }}
        />
      )}

      {keepRing && !cancelled && (
        <TgMediaProgressRing
          active={!coverReady}
          done={coverReady}
          size={compact ? 36 : 44}
          onCancel={!coverReady ? handleCancel : undefined}
        />
      )}

      {cancelled && (
        <TgMediaRetryButton size={compact ? 36 : 44} onRetry={handleRetry} />
      )}

      {showPlayOverlay && (
        <div className="pointer-events-none absolute inset-0 z-[1] grid place-content-center">
          <div
            className={`grid place-content-center rounded-full bg-black/40 shadow-sm backdrop-blur-md ${compact ? "h-9 w-9" : "h-11 w-11"}`}
          >
            <FaPlay
              className={`text-white ${compact ? "ml-[2px] h-3.5 w-3.5" : "ml-0.5 h-4 w-4"}`}
            />
          </div>
        </div>
      )}

      {timerLabel && !cancelled && (
        <div
          className={`absolute left-1.5 top-1.5 z-[3] flex items-center gap-1.5 rounded-md bg-black/55 text-white shadow-sm ${
            compact
              ? "px-1.5 py-0.5 text-[11px]"
              : "px-2 py-1 text-[12px]"
          } font-medium leading-none tabular-nums`}
        >
          <span className="pointer-events-none">{timerLabel}</span>
          {videoReady && wantAutoplay && !videoFailed && (
            <button
              type="button"
              aria-label={soundOn ? "Выключить звук" : "Включить звук"}
              onClick={toggleSound}
              className="pointer-events-auto grid place-content-center text-white/95 hover:text-white"
            >
              {soundOn ? (
                <FaVolumeHigh className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
              ) : (
                <FaVolumeXmark className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
